import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Nav,
  Form,
  InputGroup,
} from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import RepositoryList from './RepositoryList';
import CollaborationRequests from './CollaborationRequests';
import ActivityLogs from './ActivityLogs';
import Pagination from './Pagination';

const Dashboard = () => {
  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('repositories');
  const [totalPages, setTotalPages] = useState(0);
  const [perPage] = useState(25);
  const [token, setToken] = useState(null);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const searchQuery = searchParams.get('q') || '';
  const repoType = searchParams.get('type') || 'owner';
  const sort = searchParams.get('sort') || 'updated';
  const direction = searchParams.get('direction') || 'desc';

  const fetchRepositories = useCallback(
    async (page, query, type, sort, direction) => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('github_token');
        if (!token) {
          navigate('/login');
          return;
        }

        let response;
        const endpoint = query ? 'search' : 'repositories';
        const params = {
          page,
          per_page: perPage,
          type,
          sort,
          direction,
          ...(query && { q: query }),
        };

        response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/repositories${
            query ? '/search' : ''
          }`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params,
          }
        );

        setRepositories(response.data.repositories);
        setTotalPages(Math.ceil(response.data.total_count / perPage));
      } catch (err) {
        setError(
          'Failed to fetch repositories. Please try again later.'
        );
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [navigate, perPage]
  );

  useEffect(() => {
    const storedToken = localStorage.getItem('github_token');
    if (storedToken) {
      setToken(storedToken);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRepositories(
        currentPage,
        searchQuery,
        repoType,
        sort,
        direction
      );
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [
    currentPage,
    searchQuery,
    repoType,
    sort,
    direction,
    fetchRepositories,
  ]);

  const handlePageChange = (page) => {
    setSearchParams({
      q: searchQuery,
      page: page.toString(),
      type: repoType,
      sort,
      direction,
    });
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchParams({
      q: query,
      page: '1',
      type: repoType,
      sort,
      direction,
    });
  };

  const handleTypeChange = (event) => {
    const type = event.target.value;
    setSearchParams({
      q: searchQuery,
      page: '1',
      type: type,
      sort,
      direction,
    });
  };

  const handleSortChange = (event) => {
    const [newSort, newDirection] = event.target.value.split(',');
    setSearchParams({
      q: searchQuery,
      page: '1',
      type: repoType,
      sort: newSort,
      direction: newDirection,
    });
  };

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3}>
          <h4>GitHub Manager</h4>
          <div className="mt-4">
            <Nav
              className="flex-column"
              variant="pills"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
            >
              <Nav.Item>
                <Nav.Link eventKey="repositories">
                  Repositories
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="requests">
                  Collaboration Requests
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="logs">Activity Logs</Nav.Link>
              </Nav.Item>
            </Nav>
          </div>
        </Col>
        <Col md={9}>
          {activeTab === 'repositories' && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="h4">Your Repositories</h2>
                <div className="d-flex align-items-center">
                  <Form.Select
                    aria-label="Sort by"
                    value={`${sort},${direction}`}
                    onChange={handleSortChange}
                    style={{ width: '200px', marginRight: '10px' }}
                  >
                    <option value="updated,desc">Last Updated</option>
                    <option value="created,desc">Newest</option>
                    <option value="pushed,desc">
                      Recently Pushed
                    </option>
                    <option value="full_name,asc">Name (A-Z)</option>
                    <option value="full_name,desc">Name (Z-A)</option>
                    <option value="stargazers,desc">Stars</option>
                  </Form.Select>
                  <Form.Select
                    aria-label="Filter by type"
                    value={repoType}
                    onChange={handleTypeChange}
                    style={{ width: '150px', marginRight: '10px' }}
                  >
                    <option value="owner">Owned</option>
                    <option value="all">All</option>
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                    <option value="member">Member</option>
                  </Form.Select>
                  <InputGroup style={{ width: '300px' }}>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search repositories..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </InputGroup>
                </div>
              </div>
              <RepositoryList
                repositories={repositories}
                loading={loading}
                error={error}
              />
              <div className="d-flex justify-content-center mt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
          {activeTab === 'requests' && (
            <CollaborationRequests token={token} />
          )}
          {activeTab === 'logs' && <ActivityLogs />}
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
